import StackKit from "@/shared/components/kit/stack";
import TabPanel from "@mui/lab/TabPanel";
import Tab from "@mui/material/Tab";
import React, { Dispatch, SetStateAction } from "react";
import Box from "@mui/material/Box";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { InputAdornment, TextField } from "@mui/material";
import {
  numberSeparator,
  toEnglishNumber,
  toPersianNumber,
} from "@/hooks/useThousandsSeparator";

// Define OperationType
type OperationType = 'increase' | 'decrease';

interface UserCashBalanceModalProps {
  setDescription: Dispatch<SetStateAction<string>>;
  setBalanceAmount: Dispatch<SetStateAction<string>>;
  balanceAmount: string;
  description: string;
  setOperationType: Dispatch<SetStateAction<OperationType>>; // Changed from setValueUserCharge
  operationType: OperationType; // Pass operationType as prop
}

/**
 * UserCashBalanceModal
 *
 * مودال افزایش یا کاهش موجودی نقدی کاربران.
 * این کامپوننت شامل:
 * - تب‌بندی برای انتخاب نوع عملیات (افزایش / کاهش)
 * - نمایش توضیحات و قوانین مرتبط با عملیات انتخاب شده
 * - فیلد ورود مبلغ با قابلیت نمایش به صورت اعداد فارسی و جداسازی هزارگان
 * - فیلد توضیحات کوتاه برای ثبت یادداشت مربوط به عملیات
 *
 * @param setDescription - تابع برای تنظیم مقدار توضیحات
 * @param setBalanceAmount - تابع برای تنظیم مبلغ
 * @param balanceAmount - مقدار فعلی مبلغ
 * @param description - مقدار فعلی توضیحات
 * @param setOperationType - تابع برای تنظیم نوع عملیات
 * @param operationType - نوع عملیات فعلی (افزایش یا کاهش)
 *
 * @returns JSX.Element
 */

const UserCashBalanceModal = ({
  setDescription,
  setBalanceAmount,
  balanceAmount,
  description,
  operationType,
  setOperationType,
}: UserCashBalanceModalProps) => {

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    // Map tab values to operation types
    if (newValue === "1") {
      setOperationType('increase');
    } else if (newValue === "2") {
      setOperationType('decrease');
    }
  };

  // Convert operationType to tab value
  const tabValue = operationType === 'increase' ? "1" : "2";
  const isIncrease = operationType === 'increase';

  return (
    <StackKit>
      <Box sx={{ width: "100%", typography: "body1" }}>
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList onChange={handleChange} aria-label="operation type tabs">
              <Tab 
                label="افزایش اعتبار" 
                value="1" 
                sx={{
                  '&.Mui-selected': {
                    color: 'success.main',
                    fontWeight: 'bold'
                  }
                }}
              />
              <Tab 
                label="کاهش اعتبار" 
                value="2" 
                sx={{
                  '&.Mui-selected': {
                    color: 'error.main',
                    fontWeight: 'bold'
                  }
                }}
              />
            </TabList>
          </Box>
          
          {/* Tab 1: Increase Balance */}
          <TabPanel value="1">
            <StackKit gap={1}>
              <TypoGraphyKit variant="body1">
                پذیرنده گرامی ؛ در این بخش شما می‌توانید موجودی نقدی همه کاربران
                انتخاب شده در این سازمان را <strong style={{color: '#4caf50'}}>افزایش</strong> دهید.
                توجه داشته باشید که موجودی افزایش یافته در این بخش 
                به حساب نقدی کاربران اضافه شده و در سیستم ثبت می‌گردد.
              </TypoGraphyKit>
              <StackKit direction={"row"} sx={{ width: "100%" }} alignItems="center" gap={2}>
                <TypoGraphyKit variant="body1" sx={{width: "50%", textAlign: "left"}}>
                  مبلغ افزایش موجودی (ریال):{" "}
                </TypoGraphyKit>
                <TextField 
                  sx={{ width: "50%" }} 
                  placeholder={`${numberSeparator(200000)}`} 
                  value={balanceAmount ? `${toPersianNumber(numberSeparator(`${balanceAmount}`))} ` : ""}
                  onChange={(e) => {
                    const englishNumber = toEnglishNumber(`${e.target.value}`);
                    const rawValue = englishNumber.replace(/[^0-9]/g, "");
                    setBalanceAmount(rawValue);
                  }}
                  InputProps={{
                    endAdornment: (<InputAdornment position="end">ریال</InputAdornment>),
                  }}
                  error={balanceAmount ? parseFloat(balanceAmount) <= 0 : false}
                  helperText={balanceAmount && parseFloat(balanceAmount) <= 0 ? "مبلغ باید بیشتر از صفر باشد" : ""}
                />
              </StackKit>
              <StackKit direction={"row"} justifyContent={"space-between"} sx={{ width: "100%" }} alignItems="center" gap={2}>
                <TypoGraphyKit variant="body1" sx={{width: "50%", textAlign: "left"}}>
                  توضیحات :{" "}
                </TypoGraphyKit>
                <TextField 
                  sx={{ width: "50%" }} 
                  value={description} 
                  onChange={(e: any) => setDescription(e.target.value)} 
                  placeholder="توضیحات افزایش موجودی" 
                  inputProps={{ maxLength: 100 }}
                  multiline
                  rows={2}
                />
              </StackKit>
            </StackKit>
          </TabPanel>
          
          {/* Tab 2: Decrease Balance */}
          <TabPanel value="2">
            <StackKit gap={1}>
              <TypoGraphyKit variant="body1">
                پذیرنده گرامی ؛ در این بخش شما می‌توانید موجودی نقدی همه کاربران
                انتخاب شده در این سازمان را <strong style={{color: '#f44336'}}>کاهش</strong> دهید.
                توجه داشته باشید که موجودی کاهش یافته در این بخش 
                از حساب نقدی کاربران کسر شده و در سیستم ثبت می‌گردد.
              </TypoGraphyKit>
              <StackKit direction={"row"} sx={{ width: "100%" }} alignItems="center" gap={2}>
                <TypoGraphyKit variant="body1" sx={{width: "50%", textAlign: "left"}}>
                  مبلغ کاهش موجودی (ریال):{" "}
                </TypoGraphyKit>
                <TextField 
                  sx={{ width: "50%" }} 
                  placeholder={`${numberSeparator(200000)}`} 
                  value={balanceAmount ? `${toPersianNumber(numberSeparator(`${balanceAmount}`))} ` : ""}
                  onChange={(e) => {
                    const englishNumber = toEnglishNumber(`${e.target.value}`);
                    const rawValue = englishNumber.replace(/[^0-9]/g, "");
                    setBalanceAmount(rawValue);
                  }}
                  InputProps={{
                    endAdornment: (<InputAdornment position="end">ریال</InputAdornment>),
                  }}
                  error={balanceAmount ? parseFloat(balanceAmount) <= 0 : false}
                  helperText={balanceAmount && parseFloat(balanceAmount) <= 0 ? "مبلغ باید بیشتر از صفر باشد" : ""}
                />
              </StackKit>
              <StackKit direction={"row"} justifyContent={"space-between"} sx={{ width: "100%" }} alignItems="center" gap={2}>
                <TypoGraphyKit variant="body1" sx={{width: "50%", textAlign: "left"}}>
                  توضیحات :{" "}
                </TypoGraphyKit>
                <TextField 
                  sx={{ width: "50%" }} 
                  value={description} 
                  onChange={(e: any) => setDescription(e.target.value)} 
                  placeholder="توضیحات کاهش موجودی" 
                  inputProps={{ maxLength: 100 }}
                  multiline
                  rows={2}
                />
              </StackKit>
            </StackKit>
          </TabPanel>
        </TabContext>
      </Box>
    </StackKit>
  );
};

export default UserCashBalanceModal;